import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, ResponsiveContainer
} from "recharts";
import {
  Zap, ChevronLeft, RefreshCw, Save,
  Target, AlertTriangle, CheckCircle2, TrendingUp, Calendar
} from "lucide-react";

import { API } from "../lib/api";

const PILLARS = [
  { key: "purpose", label: "Purpose" },
  { key: "stakeholders", label: "Stakeholders" },
  { key: "capability", label: "Capability" },
  { key: "adaptability", label: "Adaptability" },
  { key: "team_energy", label: "Team Energy" },
  { key: "ethics", label: "Ethics" },
  { key: "resilience", label: "Resilience" }
];

const PILLAR_WEIGHTS = {
  purpose: 1.0, stakeholders: 1.5, capability: 1.0,
  adaptability: 1.3, team_energy: 1.0, ethics: 1.2, resilience: 1.0
};
const MAX_WEIGHTED = Object.values(PILLAR_WEIGHTS).reduce((s, w) => s + 5 * w, 0);

function calcScore(pillars) {
  const ws = Object.entries(PILLAR_WEIGHTS).reduce((s, [k, w]) => s + pillars[k] * w, 0);
  return Math.round((ws / MAX_WEIGHTED) * 100 * 10) / 10;
}

function calcFailure(pillars) {
  const wc = Object.values(pillars).filter((v) => v <= 2).length;
  if (wc === 0) return 10;
  if (wc === 1) return 20;
  if (wc === 2) return 55;
  return Math.min(90, 70 + (wc - 3) * 5);
}

function getRiskLevel(score) {
  if (score >= 80) return { level: "Healthy", color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" };
  if (score >= 60) return { level: "Moderate Risk", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" };
  if (score >= 40) return { level: "High Risk", color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200" };
  return { level: "Critical", color: "text-red-600", bg: "bg-red-50", border: "border-red-200" };
}

function pillarColor(s) {
  if (s >= 4) return "#10B981";
  if (s === 3) return "#F59E0B";
  return "#EF4444";
}

export default function WhatIfPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [form, setForm] = useState({
    name: "What-If Simulation",
    project_type: "IT",
    timeline_pressure: "Medium",
    team_size: 10,
    pillars: { purpose: 3, stakeholders: 3, capability: 3, adaptability: 3, team_energy: 3, ethics: 3, resilience: 3 }
  });
  const [original, setOriginal] = useState(null);
  const [feasibility, setFeasibility] = useState(null);
  const [feasibilityLoading, setFeasibilityLoading] = useState(false);

  useEffect(() => {
    if (projectId) {
      axios.get(`${API}/api/projects/${projectId}`, { withCredentials: true })
        .then(({ data }) => {
          setForm({
            name: `${data.name} (Simulation)`,
            project_type: data.project_type,
            timeline_pressure: data.timeline_pressure,
            team_size: data.team_size,
            pillars: Object.fromEntries(
              Object.entries(data.pillars).map(([k, v]) => [k, Math.round(v)])
            )
          });
          setOriginal(data);
        })
        .catch(console.error);
    }
  }, [projectId]);

  // Recompute feasibility whenever the target moves — debounced by 350ms.
  useEffect(() => {
    const baseline = original?.pillars;
    if (!baseline) { setFeasibility(null); return; }
    const target = form.pillars;
    // Only round baseline for comparison stability; the API accepts floats.
    const baselineRounded = Object.fromEntries(
      Object.entries(baseline).map(([k, v]) => [k, Math.round(v * 100) / 100])
    );
    const t = setTimeout(() => {
      setFeasibilityLoading(true);
      axios.post(`${API}/api/whatif/feasibility`, {
        baseline: baselineRounded,
        target,
        project_id: projectId,
      }, { withCredentials: true })
        .then(({ data }) => setFeasibility(data))
        .catch((e) => { console.error(e); setFeasibility(null); })
        .finally(() => setFeasibilityLoading(false));
    }, 350);
    return () => clearTimeout(t);
  }, [form.pillars, original, projectId]);

  const score = calcScore(form.pillars);
  const failure = calcFailure(form.pillars);
  const risk = getRiskLevel(score);

  const radarData = PILLARS.map((p) => ({
    pillar: p.label,
    score: form.pillars[p.key],
    original: original?.pillars?.[p.key] || null,
    fullMark: 5
  }));

  const reset = () => {
    if (original) {
      setForm((f) => ({ ...f, pillars: { ...original.pillars } }));
    } else {
      setForm((f) => ({ ...f, pillars: { purpose: 3, stakeholders: 3, capability: 3, adaptability: 3, team_energy: 3, ethics: 3, resilience: 3 } }));
    }
  };

  const saveAsNew = async () => {
    setSaving(true);
    setSaveMsg("");
    try {
      const payload = {
        name: form.name,
        project_type: form.project_type,
        timeline_pressure: form.timeline_pressure,
        stakeholder_complexity: "Medium",
        team_size: form.team_size,
        pillars: form.pillars
      };
      const { data } = await axios.post(`${API}/api/projects`, payload, { withCredentials: true });
      setSaveMsg("Saved successfully!");
      setTimeout(() => navigate(`/project/${data.id}`), 1200);
    } catch (e) {
      setSaveMsg("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8 animate-slide-up">
          <button onClick={() => navigate(projectId ? `/project/${projectId}` : "/dashboard")}
            className="flex items-center gap-1 text-sm font-ibm-plex text-slate-500 hover:text-slate-900 mb-4 transition-colors">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-5 h-5 text-blue-600" />
                <h1 className="font-outfit text-3xl font-black text-slate-900">What-If Simulation</h1>
              </div>
              <p className="font-ibm-plex text-sm text-slate-500">
                Adjust pillar scores and instantly see how your PIQ changes.
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={reset}
                className="flex items-center gap-2 border border-gray-300 text-slate-700 font-outfit font-medium px-4 py-2 rounded-lg hover:bg-gray-50 text-sm transition-colors">
                <RefreshCw className="w-3.5 h-3.5" />
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sliders */}
          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-6 animate-slide-up stagger-1">
              <h2 className="font-outfit text-base font-bold text-slate-900 mb-4">Pillar Scores</h2>
              <div className="space-y-5">
                {PILLARS.map((p) => {
                  const s = form.pillars[p.key];
                  const origS = original?.pillars?.[p.key];
                  const diff = origS ? s - origS : 0;
                  return (
                    <div key={p.key}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-outfit font-semibold text-slate-900 text-sm">{p.label}</span>
                        <div className="flex items-center gap-2">
                          {origS && diff !== 0 && (
                            <span className={`text-xs font-ibm-plex font-medium ${diff > 0 ? "text-emerald-600" : "text-red-600"}`}>
                              {diff > 0 ? "+" : ""}{diff}
                            </span>
                          )}
                          <span className="font-outfit text-xl font-black" style={{ color: pillarColor(s), width: 28, textAlign: "right" }}>{s}</span>
                        </div>
                      </div>
                      <div className="relative">
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all"
                            style={{ width: `${(s / 5) * 100}%`, background: pillarColor(s) }} />
                        </div>
                        <input
                          data-testid={`sim-pillar-${p.key}`}
                          type="range" min={1} max={5} step={1} value={s}
                          onChange={(e) => setForm((f) => ({ ...f, pillars: { ...f.pillars, [p.key]: Number(e.target.value) } }))}
                          className="absolute inset-0 w-full opacity-0 cursor-pointer"
                          style={{ height: "24px", top: "-6px" }}
                        />
                      </div>
                      <div className="flex justify-between mt-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button key={n} type="button"
                            onClick={() => setForm((f) => ({ ...f, pillars: { ...f.pillars, [p.key]: n } }))}
                            className={`w-7 h-7 rounded-full text-xs flex items-center justify-center transition-all font-outfit ${
                              s === n ? "text-white" : "text-slate-300 hover:bg-gray-100"
                            }`}
                            style={s === n ? { background: pillarColor(s) } : {}}>
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Live Results */}
          <div className="space-y-4">
            {/* Score Card */}
            <div className={`${risk.bg} ${risk.border} border rounded-xl p-6 animate-slide-up stagger-2`}>
              <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-500 mb-1">PIQ Score</p>
              <div className="flex items-end gap-2 mb-1">
                <span data-testid="sim-piq-score" className={`font-outfit text-5xl font-black ${risk.color}`}>{score}</span>
                <span className="font-ibm-plex text-slate-400 mb-1">/ 100</span>
              </div>
              <span data-testid="sim-risk-level" className={`font-outfit font-semibold ${risk.color}`}>{risk.level}</span>
              {original && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <span className="font-ibm-plex text-xs text-slate-400">
                    vs. original: {original.piq_score} ({original.risk_level})
                  </span>
                  <span className={`ml-2 text-xs font-ibm-plex font-semibold ${score > original.piq_score ? "text-emerald-600" : score < original.piq_score ? "text-red-600" : "text-slate-400"}`}>
                    {score > original.piq_score ? `+${(score - original.piq_score).toFixed(1)}` :
                      score < original.piq_score ? `${(score - original.piq_score).toFixed(1)}` : "No change"}
                  </span>
                </div>
              )}
            </div>

            {/* Failure Probability */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 animate-slide-up stagger-3">
              <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-500 mb-2">Failure Probability</p>
              <div className="flex items-end gap-2 mb-2">
                <span data-testid="sim-failure-prob" className={`font-outfit text-4xl font-black ${risk.color}`}>{failure}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-300"
                  style={{ width: `${failure}%`, background: failure >= 60 ? "#EF4444" : failure >= 40 ? "#F97316" : "#F59E0B" }} />
              </div>
            </div>

            {/* Radar Chart */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 animate-slide-up stagger-4">
              <h3 className="font-outfit text-base font-bold text-slate-900 mb-3">Pillar Visualization</h3>
              <ResponsiveContainer width="100%" height={220}>
                <RadarChart data={radarData} margin={{ top: 5, right: 20, bottom: 5, left: 20 }}>
                  <PolarGrid stroke="#E5E7EB" />
                  <PolarAngleAxis dataKey="pillar" tick={{ fontFamily: "IBM Plex Sans", fontSize: 10, fill: "#4B5563" }} />
                  <PolarRadiusAxis angle={30} domain={[0, 5]} tick={false} axisLine={false} />
                  {original && (
                    <Radar name="Original" dataKey="original" stroke="#9CA3AF" fill="#9CA3AF"
                      fillOpacity={0.1} strokeWidth={1} strokeDasharray="4 4" />
                  )}
                  <Radar name="Simulation" dataKey="score" stroke="#2563EB" fill="#2563EB"
                    fillOpacity={0.2} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
              {original && (
                <div className="flex items-center gap-4 justify-center mt-2">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-px bg-slate-400 border-dashed border" />
                    <span className="font-ibm-plex text-xs text-slate-400">Original</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-0.5 bg-blue-600" />
                    <span className="font-ibm-plex text-xs text-slate-400">Simulation</span>
                  </div>
                </div>
              )}
            </div>

            {/* Save */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 animate-slide-up stagger-5">
              <input
                data-testid="sim-name-input"
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Simulation name"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 mb-3"
              />
              <button
                data-testid="save-simulation-btn"
                onClick={saveAsNew}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white font-outfit font-semibold py-2.5 rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-60"
              >
                <Save className="w-4 h-4" />
                {saving ? "Saving..." : "Save as New Analysis"}
              </button>
              {saveMsg && (
                <p className={`text-xs font-ibm-plex mt-2 text-center ${saveMsg.includes("success") ? "text-emerald-600" : "text-red-600"}`}>
                  {saveMsg}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ─── Feasibility & Action Plan ─────────────────────────────────── */}
        <FeasibilityPanel data={feasibility} loading={feasibilityLoading} hasOriginal={!!original} />
      </div>
    </div>
  );
}

// ─── Feasibility & Action Plan panel ────────────────────────────────────────
const FEASIBILITY_STYLES = {
  realistic:   { label: "Realistic",   color: "text-emerald-700", bg: "bg-emerald-50",  border: "border-emerald-200", dot: "bg-emerald-500", icon: CheckCircle2 },
  stretch:     { label: "Stretch",     color: "text-amber-700",   bg: "bg-amber-50",    border: "border-amber-200",   dot: "bg-amber-500",   icon: TrendingUp },
  unrealistic: { label: "Unrealistic", color: "text-red-700",     bg: "bg-red-50",      border: "border-red-200",     dot: "bg-red-500",     icon: AlertTriangle },
  maintain:    { label: "Maintain",    color: "text-slate-600",   bg: "bg-slate-50",    border: "border-slate-200",   dot: "bg-slate-400",   icon: Target },
  mixed:       { label: "Mixed",       color: "text-blue-700",    bg: "bg-blue-50",     border: "border-blue-200",    dot: "bg-blue-500",    icon: TrendingUp },
};

function FeasibilityPanel({ data, loading, hasOriginal }) {
  if (!hasOriginal) {
    return (
      <div className="mt-6 bg-white border border-gray-200 rounded-xl p-6 animate-slide-up" data-testid="feasibility-panel">
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-5 h-5 text-blue-600" />
          <h2 className="font-outfit text-lg font-bold text-slate-900">Feasibility & Action Plan</h2>
        </div>
        <p className="font-ibm-plex text-sm text-slate-500">
          Open this page from a saved assessment (via <span className="font-semibold">?projectId=...</span>) to see 3-month
          and 6-month realistic targets and a phased action plan.
        </p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="mt-6 bg-white border border-gray-200 rounded-xl p-6 animate-slide-up" data-testid="feasibility-panel">
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-5 h-5 text-blue-600" />
          <h2 className="font-outfit text-lg font-bold text-slate-900">Feasibility & Action Plan</h2>
        </div>
        <p className="font-ibm-plex text-sm text-slate-500">
          {loading ? "Analysing feasibility…" : "Adjust a slider to see whether the target is realistic in 3–6 months."}
        </p>
      </div>
    );
  }

  const summary = data.summary || {};
  const overall = FEASIBILITY_STYLES[summary.overall_feasibility] || FEASIBILITY_STYLES.maintain;
  const OverallIcon = overall.icon;
  const improving = (data.pillars || []).filter((p) => p.direction === "improve");

  return (
    <div className="mt-6 space-y-4" data-testid="feasibility-panel">
      {/* Headline */}
      <div className={`border rounded-xl p-5 ${overall.bg} ${overall.border} animate-slide-up`}>
        <div className="flex items-start gap-3">
          <OverallIcon className={`w-5 h-5 mt-0.5 ${overall.color}`} />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="font-outfit text-lg font-black text-slate-900">Feasibility & Action Plan</h2>
              <span data-testid="feasibility-overall" className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-ibm-plex font-semibold ${overall.color} bg-white/60`}>
                <span className={`w-1.5 h-1.5 rounded-full ${overall.dot}`} /> {overall.label}
              </span>
            </div>
            <p className="font-ibm-plex text-sm text-slate-700">{data.headline}</p>
            {summary.horizon_note && (
              <p className="font-ibm-plex text-xs text-slate-500 mt-2">{summary.horizon_note}</p>
            )}
          </div>
        </div>

        {(summary.top_phase_1_priorities || []).length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/40">
            <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-500 mb-2">Top Phase-1 Priorities (Next 90 days)</p>
            <ul className="space-y-1.5">
              {summary.top_phase_1_priorities.map((tp, i) => (
                <li key={i} className="font-ibm-plex text-sm text-slate-700 flex gap-2">
                  <span className="text-slate-400">{i + 1}.</span>
                  <span><span className="font-semibold">{tp.label}:</span> {tp.action}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Per-pillar cards — only pillars where user requested improvement */}
      {improving.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
          <p className="font-ibm-plex text-sm text-slate-500">
            No pillar targets exceed their current level yet. Move a slider up to see a 3/6-month plan.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {improving.map((p) => <PillarFeasibilityCard key={p.pillar} p={p} />)}
        </div>
      )}
    </div>
  );
}

function PillarFeasibilityCard({ p }) {
  const style = FEASIBILITY_STYLES[p.feasibility] || FEASIBILITY_STYLES.stretch;
  const Icon = style.icon;
  const bar = (pct) => Math.max(0, Math.min(100, pct));

  return (
    <div
      data-testid={`feasibility-card-${p.pillar}`}
      className={`bg-white border ${style.border} rounded-xl p-5 animate-slide-up`}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-outfit text-base font-bold text-slate-900">{p.label}</h3>
        <span data-testid={`feasibility-${p.pillar}-verdict`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-ibm-plex font-semibold ${style.color} ${style.bg}`}>
          <Icon className="w-3 h-3" /> {style.label}
        </span>
      </div>

      {/* Baseline / Target / Realistic 3/6 month */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        <NumberChip label="Now" value={p.baseline} />
        <NumberChip label="Target" value={p.target} strong />
        <NumberChip label="3-mo real" value={p.realistic_target_3mo} tint="blue" />
        <NumberChip label="6-mo real" value={p.realistic_target_6mo} tint="emerald" />
      </div>

      {/* Gap bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="font-ibm-plex text-xs text-slate-500">Requested gap</span>
          <span className="font-ibm-plex text-xs font-semibold text-slate-700">+{p.gap.toFixed(1)}</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden relative">
          {/* 6-month realistic ceiling marker */}
          <div className="absolute top-0 left-0 h-full bg-emerald-200/70"
            style={{ width: `${bar((p.ceiling_6mo / 5) * 100 * 2)}%` }} />
          <div className="absolute top-0 left-0 h-full bg-blue-300/80"
            style={{ width: `${bar((p.ceiling_3mo / 5) * 100 * 2)}%` }} />
        </div>
        <div className="flex items-center gap-3 mt-1">
          <span className="flex items-center gap-1 font-ibm-plex text-[10px] text-slate-500"><span className="w-2 h-2 rounded-sm bg-blue-300" /> 3-mo ceiling +{p.ceiling_3mo}</span>
          <span className="flex items-center gap-1 font-ibm-plex text-[10px] text-slate-500"><span className="w-2 h-2 rounded-sm bg-emerald-200" /> 6-mo ceiling +{p.ceiling_6mo}</span>
        </div>
      </div>

      <p className="font-ibm-plex text-xs text-slate-600 mb-3">{p.timeline}</p>
      {p.note && (
        <p className="font-ibm-plex text-xs italic text-slate-500 mb-3">{p.note}</p>
      )}

      {/* Milestones */}
      {p.milestones && p.milestones.length > 0 && (
        <div className="mb-3">
          <div className="flex items-center gap-1 mb-2">
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            <span className="font-ibm-plex text-xs uppercase tracking-widest text-slate-500">Milestones</span>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {p.milestones.map((m) => (
              <div key={m.month} className="border border-gray-200 rounded-lg px-2 py-1.5">
                <div className="flex items-baseline justify-between">
                  <span className="font-outfit text-xs font-bold text-slate-900">M{m.month}</span>
                  <span className="font-ibm-plex text-xs font-semibold text-blue-600">{m.target.toFixed(1)}</span>
                </div>
                <p className="font-ibm-plex text-[10px] text-slate-500 leading-snug mt-0.5 line-clamp-2">{m.focus}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      {p.actions && (p.actions.phase_1_0_to_3_months?.length > 0 || p.actions.phase_2_3_to_6_months?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ActionColumn title="Phase 1 · 0–3 months" tint="blue"
            items={p.actions.phase_1_0_to_3_months} />
          <ActionColumn title="Phase 2 · 3–6 months" tint="emerald"
            items={p.actions.phase_2_3_to_6_months} />
        </div>
      )}
    </div>
  );
}

function NumberChip({ label, value, strong, tint }) {
  const tints = {
    blue: "text-blue-700 bg-blue-50",
    emerald: "text-emerald-700 bg-emerald-50",
    default: strong ? "text-slate-900 bg-slate-100" : "text-slate-700 bg-white border border-gray-200",
  };
  return (
    <div className={`rounded-lg px-2 py-1.5 ${tints[tint] || tints.default}`}>
      <div className="font-ibm-plex text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-outfit text-base font-black leading-none mt-0.5">
        {typeof value === "number" ? value.toFixed(1) : value}
      </div>
    </div>
  );
}

function ActionColumn({ title, tint, items }) {
  const bar = tint === "blue" ? "border-blue-300" : "border-emerald-300";
  return (
    <div className={`border-l-2 pl-3 ${bar}`}>
      <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-500 mb-1.5">{title}</p>
      {items && items.length > 0 ? (
        <ul className="space-y-1">
          {items.map((a, i) => (
            <li key={i} className="font-ibm-plex text-xs text-slate-700 leading-snug">• {a}</li>
          ))}
        </ul>
      ) : (
        <p className="font-ibm-plex text-xs text-slate-400">No specific actions — maintain rhythm.</p>
      )}
    </div>
  );
}
